(() => {
  'use strict';

  // Versioned after the production PDP-guide selector audit on 2026-07-30.
  if (window.__lanceIntentTelemetry) return;
  window.__lanceIntentTelemetry = true;

  const clean = (value, limit = 100) =>
    String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, limit);

  // Read Shopify's current purpose permissions. Never set consent on a buyer's behalf.
  const allowed = purpose => {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!privacy && typeof privacy[purpose] === 'function' && privacy[purpose]() === true;
    } catch (_) { return false; }
  };
  let readinessTimer;
  const announceReady = (attempt = 0) => {
    clearTimeout(readinessTimer);
    if (window.Shopify?.customerPrivacy && typeof window.gtag === 'function') {
      document.dispatchEvent(new CustomEvent('lance:privacy-ready'));
    } else if (attempt < 80) {
      readinessTimer = setTimeout(() => announceReady(attempt + 1), 250);
    }
  };
  const loadPrivacy = () => {
    const ready = () => { document.dispatchEvent(new CustomEvent('lance:privacy-available')); announceReady(); };
    if (window.Shopify?.customerPrivacy) { ready(); return; }
    if (!window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => { if (!error) ready(); });
  };
  loadPrivacy();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadPrivacy, {once:true});
  window.addEventListener('load', loadPrivacy, {once:true});
  document.addEventListener('visitorConsentCollected', () => announceReady());
  const emit = (name, parameters = {}) => {
    const analytics = allowed('analyticsProcessingAllowed');
    const marketing = allowed('marketingAllowed') && allowed('saleOfDataAllowed');
    if (!analytics && !marketing) return false;
    // Impressions require an analytics sink, not an unconsumed generic queue entry.
    // The finder re-measures current visibility at readiness; no historical replay.
    if (name === 'lance_finder_recommendation_shown' && (!analytics || typeof window.gtag !== 'function')) return false;
    const payload = {
      page_path: clean(window.location.pathname, 100),
      ...Object.fromEntries(
        Object.entries(parameters)
          .filter(([, value]) => value !== '' && value != null)
          .map(([key, value]) => [key, clean(value)])
      ),
    };

    // One Google dispatch path per action: gtag already writes to dataLayer.
    if (analytics && typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    }
    if (marketing && typeof window.fbq === 'function') {
      window.fbq('trackCustom', name, payload);
    }
    if (
      analytics &&
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        Promise.resolve(window.Shopify.analytics.publish(`lance:${name}`, payload)).catch(() => {});
      } catch (_) {
        // Shopify rejects an unregistered subscriber without affecting GA4/Meta.
      }
    }
    return analytics && typeof window.gtag === 'function';
  };

  // A referral is a landing observation, not a conversion or proof of AI causality.
  // Generic bing.com traffic is not classified as Copilot.
  let referralHost = '';
  try { referralHost = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : ''; } catch (_) {}
  const referralSource = [
    ['chatgpt', /(^|\.)chatgpt\.com$|(^|\.)openai\.com$/],
    ['perplexity', /(^|\.)perplexity\.ai$/],
    ['copilot', /(^|\.)copilot\.microsoft\.com$/],
    ['gemini', /(^|\.)gemini\.google\.com$/],
    ['claude', /(^|\.)claude\.ai$/]
  ].find(([, pattern]) => pattern.test(referralHost));
  let referralResolved = false;
  const observeReferral = () => {
    if (referralResolved || !referralSource || !window.Shopify?.customerPrivacy) return;
    if (!allowed('analyticsProcessingAllowed')) { referralResolved = true; return; }
    if (typeof window.gtag !== 'function') return;
    referralResolved = true;
    // Once evaluated as denied, do not replay this earlier landing after consent.
    if (allowed('analyticsProcessingAllowed')) emit('lance_agent_referral', {source: referralSource[0], referrer_host: referralHost});
  };
  document.addEventListener('lance:privacy-ready', observeReferral);
  document.addEventListener('lance:privacy-available', observeReferral);
  observeReferral();

  const shoppingSignals = new Set(['lance_finder_step','lance_finder_recommendation_shown','lance_finder_no_results','lance_shipping_estimate','lance_shipping_error','lance_drafting_select','lance_drafting_add','lance_job_selection','lance_quote_start','lance_quote_acknowledged']);
  document.addEventListener('lance:shopping-signal', event => {
    const detail = event.detail || {};
    if (shoppingSignals.has(detail.name)) detail.accepted = emit(detail.name, detail.parameters || {});
  });

  const closest = (target, selector) =>
    target instanceof Element ? target.closest(selector) : null;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      const liveVariant = closest(target, '[data-variant]');
      if (liveVariant && window.location.pathname.startsWith('/products/')) {
        emit('lance_variant_select', {
          product_handle: window.location.pathname.split('/').pop(),
          variant_id: liveVariant.dataset.variant,
          variant_title: liveVariant.textContent,
          available:
            !liveVariant.disabled &&
            !liveVariant.textContent.toUpperCase().includes('UNAVAIL'),
        });
        return;
      }

      const liveOrientation = closest(
        target,
        '[data-lab-unit],[data-lab-edge],[data-lab-dir],[data-mx]'
      );
      if (liveOrientation) {
        const step =
          liveOrientation.dataset.labUnit != null
            ? 'unit'
            : liveOrientation.dataset.labEdge != null
              ? 'edge'
              : liveOrientation.dataset.labDir != null
                ? 'direction'
                : 'preset';
        const choice =
          liveOrientation.dataset.labUnit ||
          liveOrientation.dataset.labEdge ||
          liveOrientation.dataset.labDir ||
          liveOrientation.dataset.mx ||
          '';
        emit('lance_orientation_select', { step, choice });
        return;
      }

      const finderStep = closest(target, '[data-job],[data-shape],[data-c],[data-blen]');
      if (finderStep) {
        const step =
          finderStep.dataset.job != null
            ? 'job'
            : finderStep.dataset.shape != null
              ? 'shape'
              : finderStep.dataset.c != null
                ? 'constraint'
                : 'length';
        const choice =
          finderStep.dataset.job ||
          finderStep.dataset.shape ||
          finderStep.dataset.c ||
          finderStep.dataset.blen;
        emit('lance_finder_step', { step, choice });
        return;
      }

      const finderResult = closest(target, '.lf2__btn-ink,.lf2__alt,[data-badd],[data-f-candidate]');
      if (finderResult) {
        emit('lance_finder_result', {
          action: finderResult.matches('[data-badd]') ? 'add_to_cart' : finderResult.matches('[data-f-candidate]') ? 'select_recommendation' : 'view_product',
          product_handle: finderResult.dataset.productHandle || finderResult.closest('lance-finder')?.querySelector('[data-f-tool]')?.value || '',
          destination: finderResult.getAttribute('href') || '',
          variant_id: finderResult.dataset.badd || '',
        });
        return;
      }

      const orientation = closest(
        target,
        '[data-lance-unit],[data-lance-edge],[data-lance-direction],' +
          '[data-lance-orientation-preset],[data-lance-lab-variant],' +
          '[data-lance-lab-add]'
      );
      if (orientation) {
        const step =
          orientation.dataset.lanceUnit != null
            ? 'unit'
            : orientation.dataset.lanceEdge != null
              ? 'edge'
              : orientation.dataset.lanceDirection != null
                ? 'direction'
                : orientation.dataset.lanceOrientationPreset != null
                  ? 'preset'
                  : orientation.matches('[data-lance-lab-add]')
                    ? 'add_to_cart'
                    : 'variant';
        const choice =
          orientation.dataset.lanceUnit ||
          orientation.dataset.lanceEdge ||
          orientation.dataset.lanceDirection ||
          orientation.dataset.lanceOrientationPreset ||
          orientation.dataset.variantId ||
          '';
        emit('lance_orientation_select', { step, choice });
        return;
      }

      const retailChoice = closest(target, '[data-pick]');
      if (retailChoice) {
        const [step, choice] = clean(retailChoice.dataset.pick).split(':');
        emit('lance_retail_builder', { step, choice });
        return;
      }

      const inquiry = closest(target, '[data-lance-inquiry]');
      if (inquiry) {
        emit('lance_b2b_intent', {
          action: 'open_inquiry',
          inquiry_type: inquiry.dataset.inqTitle || 'general',
        });
        return;
      }

      const tradeJob = closest(target, '[data-lance-trade-job],.mtco__job');
      if (tradeJob) {
        emit('lance_trade_job_select', {
          job:
            tradeJob.dataset.lanceTradeJob ||
            tradeJob.querySelector('.mtco__job-number')?.textContent ||
            '',
          destination: tradeJob.getAttribute('href') || '',
        });
        return;
      }

      const link = closest(target, 'a[href]');
      if (link) {
        const url = new URL(link.href, window.location.origin);
        const route = url.pathname;
        if (/^\/pages\/(teachers-rule|artists-rule|engineers-rule|makers-rule)/.test(route)) {
          emit('lance_audience_select', {
            audience: route.split('/').pop(),
            destination: route,
          });
        } else if (/^\/pages\/(lance-pro|retailers|partner)/.test(route)) {
          emit('lance_b2b_intent', {
            action: 'route_click',
            destination: route,
          });
        }
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const variant = closest(event.target, '[data-lance-variant]');
      if (variant) {
        emit('lance_variant_select', {
          product_handle: window.location.pathname.split('/').pop(),
          variant_id: variant.value,
          variant_title: variant.dataset.title,
          sku: variant.dataset.sku,
          available: variant.dataset.available,
        });
      }
    },
    true
  );

  document.addEventListener(
    'toggle',
    (event) => {
      const details = closest(
        event.target,
        '.lance-pdp-details details, [id$="__pdp_lower"] details'
      );
      if (details && details.open) {
        emit('lance_product_guide_open', {
          guide: details.querySelector('summary')?.textContent || 'product_details',
          product_handle: window.location.pathname.split('/').pop(),
        });
      }
    },
    true
  );
})();
